import { onlineManager } from "@tanstack/react-query";

/**
 * Binds TanStack Query's `onlineManager` to the browser's connectivity signal —
 * the web analogue of `apps/mobile/lib/network-status.ts`. Call ONCE at module
 * scope, before the first render.
 *
 * The immediate `update()` is the load-bearing line, and it is invisible: the
 * manager's own default listener tracks TRANSITIONS only and its initial value
 * is a hardcoded `true`. A document that LOADS with no network receives no
 * `offline` event — correct per specification, since nothing transitioned — so
 * without a seed it reports online for the whole outage. Everything downstream
 * then reads the wrong branch: `useIsOnline` takes the network-only chain into a
 * failure screen instead of the stored bundle, the sync bar drops its Offline
 * badge, the persona probes retry instead of settling into the snapshot path,
 * and the reconnect edge cannot fire because `setOnline(true)` is a no-op on a
 * manager that already believes it. Deleting the seed as redundant restores all
 * four at once, and only on the reload path — the one that matters offline.
 *
 * `navigator.onLine` lies through captive portals; that is accepted and designed
 * for. Every push failure classifies `network` and requeues, so a false "online"
 * costs one wasted run and never data — which is why there is no reachability
 * probe here.
 */
export function setupNetworkStatus(): void {
  onlineManager.setEventListener((setOnline) => {
    const update = () => {
      setOnline(navigator.onLine);
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  });
}
